/**
 * C5Frame — the "Indigo & Ink" DOM frame around the Phaser canvas (Layer 0).
 *
 * Doctrine: docs/design/ui-c5-indigo-ink.md. Visual reference: the DIRECTION C5
 * section of docs/mockups/border-ui-mockups-2026-08-16.html.
 *
 * Shape of the thing
 * ------------------
 * The frame is a real layout box, not an overlay:
 *
 *   #c5-root  (fixed, fills the viewport, centres the frame)
 *     #c5-frame
 *       .c5-band.c5-top      specimen label + DAY N
 *       #game-root           <- Phaser's canvas parent, 16:9, never covered
 *       .c5-band.c5-bottom   washi seam, ink-card vitals, hint slip, sockets
 *       .c5-dial             day-wheel, overlapping the band/painting boundary
 *
 * Because the plate slot is sized to an exact 16:9 box, Phaser's Scale.FIT
 * fills it with no letterbox of its own, and the bands are guaranteed to line
 * up with the painting's edges at every window size. The alternative — bands
 * floating over the canvas — would violate hard rule 2 (nothing occludes the
 * painting).
 *
 * State arrives exclusively through HudState events; this class never reaches
 * into a scene.
 */

import type * as Phaser from 'phaser';
import tokensCss from './c5-tokens.css?inline';
import frameCss from './c5-frame.css?inline';
import { getString } from '../locale';
import {
  onDayChanged,
  onHintChanged,
  onChromeMode,
  onSceneLabelChanged,
  onSlipShow,
  onTimeTick,
  onVitalsChanged,
  type HudTimeTickPayload,
  type HudUnsub,
  type HudVitalsPayload,
  type ChromeMode,
} from '../../game/HudState';
import { readDayCount } from './dayCount';
import {
  MOON_FRACTION,
  SUN_FRACTION,
  discRotationDeg,
  formatClock,
  fractionToDeg,
  moonIllumination,
  moonStep,
  wheelSegments,
} from './dayWheel';
import { SlipToasts } from './SlipToasts';
import { LetterboxUi } from './LetterboxUi';
import { solvePlate, PLATE_ASPECT } from './plateLayout';
import { Almanac } from './Almanac';

const STYLE_ELEMENT_ID = 'c5-styles';
const GAME_ROOT_ID = 'game-root';



/** Doctrine rule 5: below this ratio the ink stroke gutters. */
const LOW_VITAL_RATIO = 0.25;

/** How many empty quick-slot sockets the console shows in Layer 0. */
const SOCKET_COUNT = 4;

/** Soft blend width between painted disc segments, in degrees. */
const DISC_BLEND_DEG = 9;

const DISC_SEGMENT_COLOR: Record<string, string> = {
  dawn: 'var(--c5-disc-dawn)',
  day: 'var(--c5-disc-day)',
  dusk: 'var(--c5-disc-dusk)',
  night: 'var(--c5-disc-night)',
};

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export class C5Frame {
  private readonly root: HTMLDivElement;
  private readonly frame: HTMLDivElement;
  private readonly gameRoot: HTMLElement;
  private readonly label: HTMLSpanElement;
  private readonly dayText: HTMLSpanElement;
  private readonly stamp: HTMLDivElement;
  private readonly hpTrack: HTMLDivElement;
  private readonly hpFill: HTMLElement;
  private readonly mpTrack: HTMLDivElement;
  private readonly mpFill: HTMLElement;
  private readonly hint: HTMLDivElement;
  private readonly hintSpan: HTMLSpanElement;
  private readonly dial: HTMLDivElement;
  private readonly disc: HTMLDivElement;
  private readonly sun: HTMLDivElement;
  private readonly moon: HTMLDivElement;
  private readonly moonDot: HTMLElement;
  private readonly timechip: HTMLDivElement;
  private readonly slips: SlipToasts;
  private letterbox: LetterboxUi | null = null;
  private readonly almanac: Almanac;

  private game: Phaser.Game | null = null;
  private readonly unsubs: HudUnsub[] = [];
  private onResize: (() => void) | null = null;
  private day = 1;
  /**
   * How much of the console is drawn. `bare` is for a scene that is its own
   * interface — see The Small Hours, where vitality and breath bars belong to
   * a different game entirely and the sky is supposed to be the clock.
   */
  private chromeMode: ChromeMode = 'full';
  private laying = false;
  private pendingRefresh: number | null = null;

  constructor() {
    injectStyles();

    this.root = el('div');
    this.root.id = 'c5-root';
    this.root.setAttribute('aria-label', getString('c5.a11y.frame'));

    this.frame = el('div');
    this.frame.id = 'c5-frame';
    this.root.appendChild(this.frame);

    // ── top band ────────────────────────────────────────────────────────
    const top = el('div', 'c5-band c5-top');
    this.label = el('span', 'c5-paper-label');
    this.label.hidden = true;
    this.dayText = el('span', 'c5-day-text');
    top.append(this.label, this.dayText);
    this.frame.appendChild(top);

    // ── plate slot: the existing #game-root moves in here ───────────────
    this.gameRoot = ensureGameRoot();
    this.frame.appendChild(this.gameRoot);

    // ── bottom band ─────────────────────────────────────────────────────
    const bottom = el('div', 'c5-band c5-bottom');
    bottom.appendChild(el('div', 'c5-washi-seam'));

    const inkWrap = el('div', 'c5-ink-wrap');
    const card = el('div', 'c5-ink-card');
    const vitals = el('div', 'c5-ink-vitals');
    vitals.setAttribute('aria-label', getString('c5.a11y.vitals'));

    const hp = buildInkRow(getString('c5.vitals.health'), 'c5-hp');
    const mp = buildInkRow(getString('c5.vitals.mana'), 'c5-mp');
    this.hpTrack = hp.track;
    this.hpFill = hp.fill;
    this.mpTrack = mp.track;
    this.mpFill = mp.fill;
    vitals.append(hp.row, mp.row);
    card.appendChild(vitals);

    this.stamp = el('div', 'c5-mini-stamp');
    inkWrap.append(card, this.stamp);
    bottom.appendChild(inkWrap);

    this.hint = el('div', 'c5-clip-slip');
    this.hintSpan = el('span');
    this.hint.appendChild(this.hintSpan);
    this.hint.hidden = true;
    bottom.appendChild(this.hint);

    const sockets = el('div', 'c5-sockets');
    sockets.setAttribute('aria-label', getString('c5.a11y.sockets'));
    for (let i = 0; i < SOCKET_COUNT; i++) {
      const socket = el('div', 'c5-socket c5-empty');
      socket.setAttribute('aria-label', getString('c5.socket.empty'));
      sockets.appendChild(socket);
      // The first socket is the letterbox; LetterboxUi owns it from here.
      if (i === 0) this.letterbox = new LetterboxUi(socket, this.frame, {
        showSlip: (text) => this.slips.showSlip(text),
      });
    }
    bottom.appendChild(sockets);
    this.frame.appendChild(bottom);

    // ── day-wheel ───────────────────────────────────────────────────────
    const dial = el('div', 'c5-dial');
    dial.setAttribute('aria-label', getString('c5.a11y.day_wheel'));
    dial.hidden = true; // paper is earned: no clock until the world has one
    const ring = el('div', 'c5-ring');
    this.disc = el('div', 'c5-disc');
    this.disc.style.background = buildDiscGradient();
    this.disc.appendChild(el('div', 'c5-stars'));

    this.sun = el('div', 'c5-body c5-sun');
    this.sun.appendChild(el('i'));
    this.moon = el('div', 'c5-body c5-moon');
    this.moonDot = el('i');
    this.moon.appendChild(this.moonDot);
    this.disc.append(this.sun, this.moon);

    this.timechip = el('div', 'c5-timechip');
    dial.append(ring, this.disc, el('div', 'c5-pointer'), this.timechip);
    this.dial = dial;
    this.frame.appendChild(dial);

    // Bodies sit at their own fractions and ride the disc as it turns.
    this.sun.style.transform = bodyTransform(fractionToDeg(SUN_FRACTION));
    this.moon.style.transform = bodyTransform(fractionToDeg(MOON_FRACTION));

    // ── slips (inside the plate — the one sanctioned trespass) ──────────
    this.slips = new SlipToasts(this.gameRoot);

    // ── almanac: the paper the day-wheel opens onto (Layer 3) ───────────
    this.almanac = new Almanac(this.frame);
    this.almanac.bindTrigger(this.dial);

    document.body.appendChild(this.root);

    this.day = readDayCount();
    this.renderDay();
    this.layout();
  }

  /** The element Phaser should use as its canvas parent. */
  getParentId(): string {
    return GAME_ROOT_ID;
  }

  /**
   * Strip the console down to nothing, or put it back.
   *
   * Hiding the bands is only half of it: their height is baked into the plate
   * calculation, so a bare frame also *returns that space to the painting*
   * rather than leaving a black margin where the console used to be.
   */
  setChromeMode(mode: ChromeMode): void {
    if (mode === this.chromeMode) return;
    this.chromeMode = mode;
    this.frame.classList.toggle('c5-bare', mode === 'bare');
    this.layout();
  }

  getChromeMode(): ChromeMode {
    return this.chromeMode;
  }

  /** Wire the frame to the game's HudState bus. Call once, after boot. */
  attach(game: Phaser.Game): void {
    this.game = game;
    this.unsubs.push(
      onChromeMode(game, (m) => this.setChromeMode(m)),
      onSceneLabelChanged(game, (l) => this.setSceneLabel(l)),
      onVitalsChanged(game, (v) => this.setVitals(v)),
      onTimeTick(game, (t) => this.setTime(t)),
      onHintChanged(game, (h) =>
        this.setHint(h ? { key: h.key, label: h.label } : null),
      ),
      onDayChanged(game, (d) => this.setDay(d.day)),
      onSlipShow(game, (p) => this.slips.showSlip(p.text)),
    );
    this.almanac.attach(game);

    this.letterbox?.attach(game);

    // Only the window drives re-layout. Listening to `scale.resize` would
    // recurse: layout() -> scale.refresh() -> 'resize' -> layout().
    this.onResize = () => this.layout();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    this.layout();
  }

  // ── layout ────────────────────────────────────────────────────────────

  /**
   * Size the plate slot to an exact 16:9 box and scale the console around it.
   * Phaser's Scale.FIT then fills the slot precisely, so bands always align
   * with the painting's edges.
   */
  layout(): void {
    if (this.laying) return; // re-entrancy guard: refresh() can re-enter
    this.laying = true;
    try {
      this.layoutInner();
    } finally {
      this.laying = false;
    }
  }

  private layoutInner(): void {
    // solvePlate owns the hairline-border arithmetic, because how thick the
    // frame's edge is depends on whether the frame is being drawn at all.
    const { scale, bandTop, bandBottom, plateW, plateH, frameH } =
      solvePlate(window.innerWidth, window.innerHeight, this.chromeMode);

    this.root.style.setProperty('--c5-scale', String(scale));
    this.root.style.setProperty('--c5-band-top', `${bandTop}px`);
    this.root.style.setProperty('--c5-band-bottom', `${bandBottom}px`);

    this.frame.style.width = `${plateW}px`;
    this.frame.style.height = `${frameH}px`;
    this.gameRoot.style.width = `${plateW}px`;
    this.gameRoot.style.height = `${plateH}px`;

    // Phaser only re-reads the parent box when asked, and it reads it against
    // the *committed* layout — so refresh once now and once on the next frame,
    // otherwise the canvas trails a resize by one step.
    const scaleMgr = this.game?.scale;
    if (!scaleMgr) return;
    scaleMgr.refresh();
    if (this.pendingRefresh !== null) cancelAnimationFrame(this.pendingRefresh);
    this.pendingRefresh = requestAnimationFrame(() => {
      this.pendingRefresh = null;
      this.game?.scale.refresh();
    });
  }

  // ── state ─────────────────────────────────────────────────────────────

  setSceneLabel(label: string | null): void {
    const text = (label ?? '').trim();
    this.label.textContent = text;
    this.label.hidden = text.length === 0;
  }

  setVitals(v: HudVitalsPayload): void {
    applyStroke(this.hpTrack, this.hpFill, v.health, v.maxHealth);
    applyStroke(this.mpTrack, this.mpFill, v.mana, v.maxMana);
  }

  setHint(hint: { key: string; label: string } | null): void {
    if (!hint || hint.label.trim().length === 0) {
      this.hint.hidden = true;
      this.hintSpan.replaceChildren();
      return;
    }
    const kbd = el('kbd');
    kbd.textContent = hint.key;
    this.hintSpan.replaceChildren(kbd, document.createTextNode(hint.label));
    this.hint.hidden = false;
  }

  setDay(day: number): void {
    this.day = Math.max(1, Math.floor(day));
    this.renderDay();
  }

  setTime(tick: HudTimeTickPayload): void {
    if (this.dial.hidden) this.dial.hidden = false;
    this.disc.style.transform = `rotate(${discRotationDeg(tick.fraction).toFixed(2)}deg)`;
    this.timechip.textContent = formatClock(tick.fraction);

    const lit = moonIllumination(tick.moonPhase);
    this.moonDot.style.opacity = (0.25 + lit * 0.75).toFixed(2);
    this.moon.dataset.moon = moonStep(tick.moonPhase);
    this.dial.dataset.phase = tick.phase;
  }

  /** Show a loose slip on the painting corner. */
  showSlip(text: string): void {
    this.slips.showSlip(text);
  }

  // ── teardown ──────────────────────────────────────────────────────────

  destroy(): void {
    for (const off of this.unsubs) off();
    this.unsubs.length = 0;
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      this.onResize = null;
    }
    if (this.pendingRefresh !== null) {
      cancelAnimationFrame(this.pendingRefresh);
      this.pendingRefresh = null;
    }
    this.letterbox?.destroy();
    this.letterbox = null;
    this.slips.destroy();
    this.almanac.destroy();
    // Hand the canvas parent back to the document before removing the frame.
    this.gameRoot.style.width = '';
    this.gameRoot.style.height = '';
    document.body.appendChild(this.gameRoot);
    this.root.remove();
    this.game = null;
  }

  private renderDay(): void {
    this.dayText.textContent = getString('c5.day.band').replace('{0}', String(this.day));
    this.stamp.textContent = String(this.day);
  }
}

// ── module-level helpers ───────────────────────────────────────────────────

function injectStyles(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `${tokensCss}\n${frameCss}`;
  document.head.appendChild(style);
}

function ensureGameRoot(): HTMLElement {
  const existing = document.getElementById(GAME_ROOT_ID);
  if (existing) return existing;
  const node = document.createElement('div');
  node.id = GAME_ROOT_ID;
  document.body.appendChild(node);
  return node;
}

function buildInkRow(
  labelText: string,
  trackClass: string,
): { row: HTMLDivElement; track: HTMLDivElement; fill: HTMLElement } {
  const row = el('div', 'c5-ink-row');
  const label = el('em');
  label.textContent = labelText;
  const track = el('div', `c5-ink-track ${trackClass}`);
  const fill = el('i');
  track.appendChild(fill);
  for (const pct of [25, 50, 75]) {
    const tick = el('u');
    tick.style.left = `${pct}%`;
    track.appendChild(tick);
  }
  row.append(label, track);
  return { row, track, fill };
}

/**
 * Conic gradient for the painted disc, authored straight from the shared
 * phase boundaries so the disc can never disagree with the simulation.
 */
function buildDiscGradient(): string {
  const stops: string[] = [];
  for (const seg of wheelSegments()) {
    const color = DISC_SEGMENT_COLOR[seg.phase] ?? 'var(--c5-disc-night)';
    const start = seg.startDeg === 0 ? 0 : seg.startDeg + DISC_BLEND_DEG;
    const end = seg.endDeg === 360 ? 360 : seg.endDeg - DISC_BLEND_DEG;
    stops.push(`${color} ${start.toFixed(1)}deg ${Math.max(start, end).toFixed(1)}deg`);
  }
  return `conic-gradient(from 0deg, ${stops.join(', ')})`;
}

/** Place a celestial body at a disc-local angle, on the disc's orbit radius. */
function bodyTransform(angleDeg: number): string {
  return `rotate(${angleDeg.toFixed(2)}deg) translate(0, calc(-28 * var(--c5-u)))`;
}

function applyStroke(
  track: HTMLElement,
  fill: HTMLElement,
  value: number,
  max: number,
): void {
  const safeMax = max > 0 ? max : 1;
  const ratio = clamp(value / safeMax, 0, 1);
  fill.style.width = `${(ratio * 100).toFixed(1)}%`;
  track.classList.toggle('c5-low', ratio > 0 && ratio < LOW_VITAL_RATIO);
  track.setAttribute('aria-valuenow', String(Math.round(value)));
  track.setAttribute('aria-valuemax', String(Math.round(safeMax)));
}

// ── singleton wiring (main.ts) ─────────────────────────────────────────────

let activeFrame: C5Frame | null = null;

/** Create the frame (before `new Phaser.Game`) so #game-root is in place. */
export function mountC5Frame(): C5Frame {
  if (!activeFrame) activeFrame = new C5Frame();
  return activeFrame;
}

/** The mounted frame, or null when the C5 flag is off. */
export function getC5Frame(): C5Frame | null {
  return activeFrame;
}

export function unmountC5Frame(): void {
  activeFrame?.destroy();
  activeFrame = null;
}
